import { createLazyResource, createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { loadStripe } from '@stripe/stripe-js';
import { createMemo } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { ownerRepository } from '~/repositories/ownersRepository';
import { paymentAccountsRepository } from '~/repositories/paymentAccounts';
import { tenantRepository } from '~/repositories/tenantRepository';
import { vendorRepo } from '~/repositories/vendorRepository';
import { PaymentAccountProvider } from '~/swagger/portal/Api';
import { envConfig } from '~/utils/env';
import type {
  CreateOwnerPaymentAccountDto,
  CreateTenantPaymentAccountDto,
  CreateVendorPaymentAccountDto,
  PaymentAccountDto,
} from '~/swagger/portal/Api';

export const [PortalPaymentAccountsProvider, usePortalPaymentAccounts] = createMagicDoorContext('portalPaymentAccounts', () => {
  // --- Company-level (same as former CompanyPaymentMethodsContext) ---
  const [paymentAccounts, paymentAccountsActions] = createLazyResource(paymentAccountsRepository.getPaymentAccounts);
  const getStripeCheckoutInfo = createMutation(paymentAccountsRepository.checkout);
  const removePaymentAccount = createMutation(paymentAccountsRepository.removePaymentAccount);
  const createPaymentAccount = createMutation(paymentAccountsRepository.createPaymentAccount);
  const getStripe = createMutation(() => loadStripe(envConfig.stripeKey));

  const pollCheckoutResult = (stripeSessionId: string) => {
    const startAt = Date.now();
    const maxWaitTime = 1000 * 60 * 2;
    const { resolve, reject, promise } = Promise.withResolvers<PaymentAccountDto>();

    (async function check() {
      const elapsed = Date.now() - startAt;
      if (elapsed > maxWaitTime) {
        reject(new Error('Checkout not completed'));
        return;
      }
      const result = await paymentAccountsRepository.createPaymentAccount({ stripeSessionId }).catch(() => null);
      if (result?.id) {
        resolve(result);
        return;
      }
      setTimeout(check, 1000);
    })();

    return promise;
  };

  const validatedPaymentAccounts = createMemo(() => paymentAccounts()?.paymentAccounts.filter((account) => account.mapped));

  const validatedPaymentAccountsOptions = createMemo(
    () =>
      validatedPaymentAccounts()?.map((account) => {
        let label: string = account.accountProvider;

        if (account.accountProvider === PaymentAccountProvider.Plaid) {
          label += ` - ${account.plaid?.plaidAccountType.toLowerCase()} - ${account.accountMask}`;
        } else if (account.accountProvider === PaymentAccountProvider.Stripe) {
          label += ` - ${account.stripe?.expMonth}/${account.stripe?.expYear} - ${account.accountMask}`;
        }

        return {
          label,
          value: account.id,
        };
      }) ?? []
  );

  // --- Entity-level: Owner ---
  const [ownerPaymentAccounts, getPaymentAccountsForOwner, ownerPaymentAccountsActions] = createTriggerResource((ownerId: string) =>
    ownerRepository.getOwnerPaymentAccounts(ownerId)
  );

  const addPaymentAccountForOwner = createMutation(async (ownerId: string, payload: CreateOwnerPaymentAccountDto) => {
    const res = await ownerRepository.addOwnerPaymentAccount(ownerId, payload);
    ownerPaymentAccountsActions.refetch();
    return res;
  });

  const deletePaymentAccountForOwner = createMutation(async (ownerId: string, paymentAccountId: string) => {
    await ownerRepository.deleteOwnerPaymentAccount(ownerId, paymentAccountId);
    ownerPaymentAccountsActions.refetch();
  });

  // --- Entity-level: Tenant ---
  const [tenantPaymentAccounts, getPaymentAccountsForTenant, tenantPaymentAccountsActions] = createTriggerResource((tenantId: string) =>
    tenantRepository.getPaymentAccounts(tenantId)
  );

  const addPaymentAccountForTenant = createMutation(async (tenantId: string, payload: CreateTenantPaymentAccountDto) => {
    const res = await tenantRepository.addTenantPaymentAccount(tenantId, payload);
    tenantPaymentAccountsActions.refetch();
    return res;
  });

  const deletePaymentAccountForTenant = createMutation(async (tenantId: string, paymentAccountId: string) => {
    await tenantRepository.deleteTenantPaymentAccount(tenantId, paymentAccountId);
    tenantPaymentAccountsActions.refetch();
  });

  // --- Entity-level: Vendor ---
  const [vendorPaymentAccounts, getPaymentAccountsForVendor, vendorPaymentAccountsActions] = createTriggerResource((vendorId: string) =>
    vendorRepo.getVendorPaymentAccounts(vendorId)
  );

  const addPaymentAccountForVendor = createMutation(async (vendorId: string, payload: CreateVendorPaymentAccountDto) => {
    const res = await vendorRepo.addVendorPaymentAccount(vendorId, payload);
    vendorPaymentAccountsActions.refetch();
    return res;
  });

  const deletePaymentAccountForVendor = createMutation(async (vendorId: string, paymentAccountId: string) => {
    await vendorRepo.deleteVendorPaymentAccount(vendorId, paymentAccountId);
    vendorPaymentAccountsActions.refetch();
  });

  return {
    // Company-level
    paymentAccounts,
    paymentAccountsActions,
    getStripe,
    getStripeCheckoutInfo,
    pollCheckoutResult,
    validatedPaymentAccounts,
    validatedPaymentAccountsOptions,
    removePaymentAccount,
    createPaymentAccount,
    // Owner entity
    ownerPaymentAccounts,
    getPaymentAccountsForOwner,
    ownerPaymentAccountsActions,
    addPaymentAccountForOwner,
    deletePaymentAccountForOwner,
    // Tenant entity
    tenantPaymentAccounts,
    getPaymentAccountsForTenant,
    tenantPaymentAccountsActions,
    addPaymentAccountForTenant,
    deletePaymentAccountForTenant,
    // Vendor entity
    vendorPaymentAccounts,
    getPaymentAccountsForVendor,
    vendorPaymentAccountsActions,
    addPaymentAccountForVendor,
    deletePaymentAccountForVendor,
  };
});
